import { useState } from "react";
import type { PageSearch } from "@bfc-software/bfc-web-ui/lib/cursor-pagination";

import type { TaskFiltersSearch } from "./filters";
import { assignModeOf, type AssignMode } from "./task-model";
import type { NonEmptyArray } from "./eligibility";
import type { ActivatedTask, Task } from "./types";

/** What a request is, for clearing: everything that changes the rows on screen, minus the drawer. */
export function requestKeyOf(
  search: TaskFiltersSearch,
  page: PageSearch,
): string {
  // `JSON.stringify` drops `undefined` keys, so blanking the two drawer params leaves them out.
  return JSON.stringify([
    { ...search, drawerPlanId: undefined, drawerTaskId: undefined },
    page.cursor ?? null,
    page.dir ?? null,
  ]);
}

/**
 * What is ticked: nothing, or at least one whole task, all of one `AssignMode` (the wire's
 * `individual_selector` and `reassign_selector` do not mix).
 */
export type SelectionState =
  | { kind: "empty" }
  | { kind: "ticked"; mode: AssignMode; tasks: NonEmptyArray<ActivatedTask> };

export interface TaskSelection {
  state: SelectionState;
  /** For the checkbox column. */
  isSelected: (task: ActivatedTask) => boolean;
  /**
   * Whether the checkbox is live: the task takes some `AssignMode`, and that is the mode of
   * what is already ticked. A ticked task stays live, so it can be unticked.
   */
  isSelectable: (task: Task) => boolean;
  /** Ticks or unticks. A task that is not `isSelectable` is ignored. */
  toggle: (task: Task) => void;
  clear: () => void;
}

/** Keyed by `Task.taskUlid`, never the warehouse number. */
type Ticked = ReadonlyMap<string, ActivatedTask>;

const EMPTY: Ticked = new Map();

/**
 * Selection held by the tab route, not the URL: a set of tasks nobody would paste to a colleague.
 * Holds the tasks themselves (snapshots from the page they were ticked on), because the banner
 * needs their eligibility; a refetch does not refresh them, and the service's 409 covers a stale one.
 * It belongs to one `requestKey`; when the key changes (a filter, the chip, the page) the held set
 * is simply no longer the current key's and reads as empty. No effect, no stale flash. Same pattern
 * the drawer hook uses to agree two halves before trusting either.
 */
export function useTaskSelection(requestKey: string): TaskSelection {
  const [state, setState] = useState<{
    key: string;
    byUlid: Ticked;
  }>({ key: requestKey, byUlid: EMPTY });

  const byUlid = state.key === requestKey ? state.byUlid : EMPTY;
  const [first, ...rest] = byUlid.values();
  const mode = first === undefined ? null : assignModeOf(first);
  const selection: SelectionState =
    first === undefined || mode === null
      ? { kind: "empty" }
      : { kind: "ticked", mode, tasks: [first, ...rest] };

  const isSelectable = (task: Task) => {
    const taskMode = assignModeOf(task);
    if (taskMode === null) return false;
    return (
      mode === null ||
      mode === taskMode ||
      (task.kind === "activated" && byUlid.has(task.taskUlid))
    );
  };

  const toggle = (task: Task) => {
    if (task.kind !== "activated" || !isSelectable(task)) return;
    setState((prev) => {
      const base = prev.key === requestKey ? prev.byUlid : EMPTY;
      const next = new Map(base);
      if (next.has(task.taskUlid)) {
        next.delete(task.taskUlid);
      } else {
        next.set(task.taskUlid, task);
      }
      return { key: requestKey, byUlid: next };
    });
  };

  const clear = () => setState({ key: requestKey, byUlid: EMPTY });

  return {
    state: selection,
    isSelected: (task) => byUlid.has(task.taskUlid),
    isSelectable,
    toggle,
    clear,
  };
}
